import { FC } from "react";
import { Star } from "@/icons";
import cn from "classnames";
import Image from "next/image";

export type RatingProps = {
  rating: number;
  maxRating?: number;
  className?: string;
  logo?: string;
  onG2Text?: string;
};

const roundRating = (rating: number): number => {
  const integerPart = Math.floor(rating);
  const decimalPart = Math.round((rating - integerPart) * 10) / 10;

  if (decimalPart <= 0.2) {
    // Round down to full star
    return integerPart;
  } else if (decimalPart >= 0.3 && decimalPart <= 0.7) {
    // Round to half star
    return integerPart + 0.5;
  } else {
    // Round up to full star
    return integerPart + 1;
  }
};

export const Rating: FC<RatingProps> = ({
  rating,
  maxRating = 5,
  className,
  logo,
  onG2Text,
}) => {
  const roundedRating = roundRating(rating);
  const stars = [];

  for (let i = 1; i <= maxRating; i++) {
    const isFullStar = i <= Math.floor(roundedRating);
    const isHalfStar =
      i === Math.ceil(roundedRating) && roundedRating % 1 !== 0;

    stars.push(
      <div key={i} className="relative inline-block">
        <Star aria-hidden="true" className="text-swdc-gray-100 !mb-0 h-6 w-6" />

        {isFullStar && (
          <div className="absolute top-0 left-0 w-full overflow-hidden">
            <Star
              aria-hidden="true"
              className="text-swdc-orange-400 !mb-0 h-6 w-6"
            />
          </div>
        )}

        {isHalfStar && (
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star
              aria-hidden="true"
              className="text-swdc-orange-400 !mb-0 h-6 w-6"
            />
          </div>
        )}
      </div>,
    );
  }

  return (
    <>
      <div className="flex h-6 flex-row items-center gap-3">
        {logo && (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.30)]">
            <Image
              src={logo}
              alt="logo"
              width={16}
              height={16}
              className="h-4 w-4 object-contain"
            />
          </div>
        )}
        <div
          className={cn("flex items-center gap-2", className)}
          role="img"
          aria-label={`Rating: ${roundedRating} out of ${maxRating} stars`}
        >
          {stars}
          <div className="flex items-baseline">
            <span className="text-body-secondary text-body-xs font-bold">
              {rating}
            </span>
            <span className="text-body-secondary text-body-xs ml-1 font-normal">{`/ ${maxRating}${onG2Text ? ` ${onG2Text}` : ""}`}</span>
          </div>
        </div>
      </div>
    </>
  );
};
